import React, { useState, useRef, useMemo, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea,
} from 'recharts';
import { themeColors } from '../theme';
import { exportCSV, exportPNG, exportSVG } from '../utils/export';
import { assignLabelSlots } from '../utils/labelLayout';
import BandgapDefectSchematic from './BandgapDefectSchematic';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;
const PL_CHART_ID = 'pl-analysis-chart-container';

// ───────────────────────────────────────────────────────────────────────────
// Parsers / helpers
// ───────────────────────────────────────────────────────────────────────────
function parsePLFile(text) {
  const rows = [];
  for (const raw of String(text).split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('#') || line.startsWith('%')) continue;
    const parts = line.split(/[\s,;\t]+/).filter(Boolean);
    if (parts.length < 2) continue;
    const x = Number(parts[0]);
    const y = Number(parts[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    rows.push({ x, y });
  }
  if (rows.length < 2) return null;
  const yMax = Math.max(...rows.map(r => Math.abs(r.y)), 1e-300);
  return rows.map(r => ({ x: r.x, y: Math.max(0, r.y / yMax) }));
}

function smooth(arr) {
  if (arr.length < 5) return arr.slice();
  const out = arr.slice();
  for (let i = 2; i < arr.length - 2; i++) {
    out[i] = {
      x: arr[i].x,
      y: (arr[i - 2].y + arr[i - 1].y + arr[i].y + arr[i + 1].y + arr[i + 2].y) / 5,
    };
  }
  return out;
}

// ── Savitzky-Golay smoothing ────────────────────────────────────────────────
// Least-squares polynomial convolution over a symmetric window. Coefficients
// are the top row of (AᵀA)⁻¹Aᵀ where A[i][k] = i^k, i = -h..h — the value of
// the fitted polynomial at the window center. Assumes ~uniform x spacing
// (true for typical spectrometer exports); edges use mirror padding.

// Gauss-Jordan inversion for the tiny (order+1)² normal matrix.
function invertMatrix(M) {
  const n = M.length;
  const a = M.map((row, i) => [
    ...row, ...Array.from({ length: n }, (_, j) => (i === j ? 1 : 0)),
  ]);
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++)
      if (Math.abs(a[r][col]) > Math.abs(a[piv][col])) piv = r;
    if (Math.abs(a[piv][col]) < 1e-12) return null;
    [a[col], a[piv]] = [a[piv], a[col]];
    const d = a[col][col];
    for (let j = 0; j < 2 * n; j++) a[col][j] /= d;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = a[r][col];
      if (f === 0) continue;
      for (let j = 0; j < 2 * n; j++) a[r][j] -= f * a[col][j];
    }
  }
  return a.map(row => row.slice(n));
}

function sgCoefficients(windowSize, order) {
  const h = Math.floor(windowSize / 2);
  const m = order + 1;
  // pow[i][k] = (i - h)^k for k = 0..2*order
  const pow = [];
  for (let i = -h; i <= h; i++) {
    const row = [1];
    for (let k = 1; k <= 2 * order; k++) row.push(row[k - 1] * i);
    pow.push(row);
  }
  const AtA = Array.from({ length: m }, (_, j) =>
    Array.from({ length: m }, (_, k) =>
      pow.reduce((s, row) => s + row[j + k], 0)));
  const inv = invertMatrix(AtA);
  if (!inv) return null;
  return pow.map(row => {
    let w = 0;
    for (let k = 0; k < m; k++) w += inv[0][k] * row[k];
    return w;
  });
}

// arr: [{x, y}] → smoothed copy. Returns an unmodified copy when the trace
// is shorter than the window (nothing sensible to fit).
function savitzkyGolay(arr, windowSize, order) {
  if (!arr || arr.length < windowSize || windowSize < 3) {
    return arr ? arr.slice() : arr;
  }
  const coeff = sgCoefficients(windowSize, order);
  if (!coeff) return arr.slice();
  const h = Math.floor(windowSize / 2);
  const n = arr.length;
  const yAt = (i) => {
    let j = i;
    if (j < 0) j = -j;                       // mirror pad left
    if (j >= n) j = 2 * n - 2 - j;           // mirror pad right
    return arr[j].y;
  };
  return arr.map((p, i) => {
    let s = 0;
    for (let k = -h; k <= h; k++) s += coeff[k + h] * yAt(i + k);
    return { x: p.x, y: Math.max(0, s) };
  });
}

const SG_WINDOWS = [5, 9, 15, 21];

function findPeaks(arr, threshold = 0.1, maxPeaks = 8, preSmoothed = false) {
  if (arr.length < 3) return [];
  // When the caller already smoothed the data (Savitzky-Golay), skip the
  // built-in 5-point moving average so peaks come from the SG curve.
  const sm = preSmoothed ? arr : smooth(arr);
  const yMax = Math.max(...sm.map(p => p.y), 1e-12);
  const cutoff = threshold * yMax;
  const peaks = [];
  for (let i = 1; i < sm.length - 1; i++) {
    if (sm[i].y > sm[i - 1].y && sm[i].y > sm[i + 1].y && sm[i].y >= cutoff) {
      peaks.push({ x: sm[i].x, y: sm[i].y });
    }
  }
  peaks.sort((a, b) => b.y - a.y);
  return peaks.slice(0, maxPeaks);
}

// hc/q — E[eV] = 1239.84193 / λ[nm]. One constant so the VIEW toggle,
// peak bookkeeping, and zoom-domain conversion all agree exactly.
const EV_NM = 1239.84193;
const eVtoNm = (eV) => EV_NM / Math.max(eV, 1e-6);
const nmToEV = (nm) => EV_NM / Math.max(nm, 1e-6);

const PEAK_COLORS = [
  '#ff6b6b', '#ffd700', '#00ff88', '#00d4ff',
  '#ff00ff', '#ff8800', '#88ff00', '#8800ff',
];
const SPECTRUM_COLORS = [
  '#00d4ff', '#ff6b6b', '#ffd700',
  '#00ff88', '#ff00ff', '#ff8800',
];

// ───────────────────────────────────────────────────────────────────────────
export default function PLAnalysisPanel({ theme = 'dark', onApplyDefects, device }) {
  // Active-device absorber (thickest absorber layer): its bandgap and
  // trap list drive the same band-diagram schematic as the PL/TRPL tab —
  // available immediately, no peak-marking required.
  const deviceAbsorber = React.useMemo(() => {
    const layers = device?.absorber?.layers || [];
    if (!layers.length) return null;
    return layers.reduce((a, b) =>
      ((b.thickness_um || 0) > (a.thickness_um || 0) ? b : a));
  }, [device]);
  const deviceEg = Number.isFinite(deviceAbsorber?.Eg) ? deviceAbsorber.Eg : null;
  const deviceDefects = React.useMemo(() =>
    (deviceAbsorber?.traps || []).map((t, i) => ({
      id: t.id || i,
      name: t.name || `${t.type || 'trap'} ${Number(t.Et ?? 0).toFixed(2)} eV`,
      Et_eV: t.Et,
      reference: t.Et_ref === 'Ec' ? 'from_Ec' : 'from_Ev',
    })), [deviceAbsorber]);

  const c = themeColors(theme);

  // Sample / device context (shared across spectra)
  const [material,   setMaterial]   = useState('CdTe');
  const [thickness,  setThickness]  = useState(3.5);
  const [tempK,      setTempK]      = useState(300);
  const [notes,      setNotes]      = useState('');
  const [laserNm,    setLaserNm]    = useState('');

  const [subTab,    setSubTab]    = useState('pl');
  const [unitMode,  setUnitMode]  = useState('nm');
  const [plError,   setPlError]   = useState(null);

  // Savitzky-Golay smoothing for uploaded spectra. sgWindow = 0 → off.
  // Applied before peak detection; the plot shows raw (faint) + smoothed
  // (solid) whenever it is on.
  const [sgWindow, setSgWindow] = useState(0);
  const [sgOrder,  setSgOrder]  = useState(2);

  // Multi-spectrum state.  Each entry:
  //   { id, name, color, raw, meta, peaks, defectResults, isExpanded,
  //     visible, isIdentifying, identifyError }
  const [spectra,     setSpectra]     = useState([]);
  const [defectQueue, setDefectQueue] = useState([]);

  // ── Chart zoom state ────────────────────────────────────────────
  // zoomDomain: null = full view; [xMin, xMax] = active zoom.
  // dragRange: tracks the in-progress drag selection while the user is
  // holding the mouse button down inside the chart.
  const [zoomDomain, setZoomDomain] = useState(null);
  const [dragRange, setDragRange] = useState({ start: null, end: null, dragging: false });

  // Global VIEW toggle (chart x-axis only). The per-spectrum unit select
  // (setSpectrumUnit) interprets the UPLOADED data; this merely converts
  // the display. Peaks keep canonical nm/eV pairs, so only the axis and
  // an active zoom window need converting — E = EV_NM/λ inverts the axis
  // order, hence the min/max re-sort.
  function setViewUnit(next) {
    if (next === unitMode) return;
    setUnitMode(next);
    setZoomDomain(d => {
      if (!d) return d;
      const a = EV_NM / Math.max(d[0], 1e-6);
      const b = EV_NM / Math.max(d[1], 1e-6);
      return [Math.min(a, b), Math.max(a, b)];
    });
  }

  function onChartMouseDown(e) {
    // e.activeLabel is the x value at the mouse position
    if (e == null || e.activeLabel == null) return;
    setDragRange({ start: e.activeLabel, end: e.activeLabel, dragging: true });
  }
  function onChartMouseMove(e) {
    if (!dragRange.dragging) return;
    if (e == null || e.activeLabel == null) return;
    setDragRange(prev => ({ ...prev, end: e.activeLabel }));
  }
  // `fromLeave` — mouse left the chart mid-press: finish any drag-zoom but
  // never treat it as a click (which would move the peak preview).
  function onChartMouseUp(fromLeave = false) {
    if (!dragRange.dragging) return;
    const { start, end } = dragRange;
    setDragRange({ start: null, end: null, dragging: false });
    if (start == null || end == null) return;
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    // Require a non-trivial selection — single-click shouldn't zoom.
    if (hi - lo > Math.abs(hi) * 0.001 + 1e-6) {
      setZoomDomain([lo, hi]);
    } else if (!fromLeave) {
      // Plain click → place / move the add-peak preview line.
      placePreview(end);
    }
  }
  function resetZoom() { setZoomDomain(null); }

  // ── Click-to-add peak preview ───────────────────────────────────
  // { eV, nm, y } snapped to the nearest data point of the target
  // (expanded / most recent) spectrum, stored canonically so it survives
  // the view-unit toggle. null = no preview. Enter confirms, Escape (or
  // Clear All) dismisses, clicking elsewhere on the chart moves it.
  const [peakPreview, setPeakPreview] = useState(null);

  const fileRef            = useRef();
  const addSpectrumInputRef = useRef();

  // ── Stage 2: publish to WorkspaceContext (pl_analysis block) ─────
  const publish = useContext(WorkspaceContextSetter);
  useEffect(() => {
    const laserVal = laserNm === '' ? null : parseFloat(laserNm);
    publish('pl_analysis', {
      material,
      thickness_um: thickness,
      temperature_K: tempK,
      notes,
      laser_nm: Number.isFinite(laserVal) ? laserVal : null,
      sub_tab: subTab,
      unit_mode: unitMode,
      spectra_count: spectra.length,
      defect_queue: (defectQueue || []).map(d => ({
        peak_eV: d.peak_eV, identity: d.name,
        Et_eV: d.Et_eV, Nt: d.Nt,
        trap_type: d.trap_type, confidence: d.confidence,
      })),
      pl_error: plError,
    });
  }, [publish, material, thickness, tempK, notes, laserNm,
      subTab, unitMode, spectra.length, defectQueue, plError]);

  // ── Spectrum operations ─────────────────────────────────────────
  // Apply the current SG smoothing setting (if any) to a raw trace.
  function applySmoothing(raw) {
    return sgWindow > 0 ? savitzkyGolay(raw, sgWindow, sgOrder) : raw;
  }

  // `parsed` should already be smoothed (via applySmoothing) when SG is on;
  // pass preSmoothed accordingly so findPeaks skips its moving average.
  function detectPeaksFor(parsed, isWavelength, laserVal, preSmoothed = false) {
    let detected = findPeaks(parsed, 0.1, 8, preSmoothed).map(p => {
      const nm = isWavelength ? p.x : eVtoNm(p.x);
      const eV = isWavelength ? nmToEV(p.x) : p.x;
      return { x: p.x, y: p.y, nm, eV, manual: false };
    });
    if (Number.isFinite(laserVal) && laserVal > 0) {
      detected = detected.filter(pk => Math.abs(pk.nm - laserVal) > 10);
    }
    return detected.map((pk, i) => ({
      ...pk, color: PEAK_COLORS[i % PEAK_COLORS.length],
    }));
  }

  async function handleFile(file) {
    if (!file) return;
    setPlError(null);
    try {
      const text = await file.text();
      const parsed = parsePLFile(text);
      if (!parsed) {
        setPlError('Could not parse — expected 2 columns (X, intensity).');
        return;
      }
      const xs = parsed.map(p => p.x);
      const xMax = Math.max(...xs);
      // xMax-threshold heuristic — meV / cm⁻¹ / µm files would be
      // misread, so the detected unit is shown per spectrum with an
      // explicit override selector (unitChoice below).
      const detectedIsWavelength = xMax > 100;
      const isWavelength = detectedIsWavelength;   // unitChoice starts 'auto'
      // Only switch unit mode on first upload
      if (spectra.length === 0) {
        setUnitMode(isWavelength ? 'nm' : 'eV');
      }
      const peaks = detectPeaksFor(applySmoothing(parsed), isWavelength,
                                   parseFloat(laserNm), sgWindow > 0);
      setSpectra(prev => {
        const colorIdx = prev.length % SPECTRUM_COLORS.length;
        const newSpec = {
          id: Date.now() + Math.random(),
          name: file.name,
          color: SPECTRUM_COLORS[colorIdx],
          raw: parsed,
          meta: {
            filename: file.name,
            n: parsed.length,
            xMin: Math.min(...xs),
            xMax,
            isWavelength,
            detectedIsWavelength,
            unitChoice: 'auto',   // 'auto' | 'nm' | 'eV'
          },
          peaks,
          defectResults: [],
          isExpanded: true,
          visible: true,
          isIdentifying: false,
          identifyError: null,
        };
        // Collapse all previous spectra; only the new one stays expanded.
        return prev.map(s => ({ ...s, isExpanded: false })).concat(newSpec);
      });
    } catch (e) {
      setPlError(`Read failed: ${e.message}`);
    }
  }

  function clearAll() {
    setSpectra([]); setDefectQueue([]); setPlError(null);
    setPeakPreview(null); setZoomDomain(null);
  }
  function removeSpectrum(id) {
    setSpectra(prev => prev.filter(s => s.id !== id));
  }
  function toggleVisible(id) {
    setSpectra(prev => prev.map(s =>
      s.id === id ? { ...s, visible: !s.visible } : s));
  }
  function toggleExpanded(id) {
    setSpectra(prev => prev.map(s =>
      s.id === id ? { ...s, isExpanded: !s.isExpanded } : s));
  }
  // Per-spectrum X-unit override ('auto' keeps the xMax auto-detection).
  // Re-interpreting the unit changes every peak's nm/eV values, so peaks
  // are re-detected and any AI defect results are cleared as stale.
  function setSpectrumUnit(id, choice) {
    setSpectra(prev => prev.map(s => {
      if (s.id !== id) return s;
      const isWavelength = choice === 'auto'
        ? s.meta.detectedIsWavelength
        : choice === 'nm';
      if (isWavelength === s.meta.isWavelength) {
        return { ...s, meta: { ...s.meta, unitChoice: choice } };
      }
      return {
        ...s,
        meta: { ...s.meta, unitChoice: choice, isWavelength },
        peaks: detectPeaksFor(applySmoothing(s.raw), isWavelength,
                              parseFloat(laserNm), sgWindow > 0),
        defectResults: [],
        identifyError: null,
      };
    }));
  }

  // Re-detect peaks on every loaded spectrum when the smoothing setting
  // changes. Manual peaks are preserved; AI defect results are cleared as
  // stale (they were derived from the previous peak set).
  useEffect(() => {
    setSpectra(prev => prev.map(s => {
      const auto = detectPeaksFor(applySmoothing(s.raw), s.meta.isWavelength,
                                  parseFloat(laserNm), sgWindow > 0);
      const manual = s.peaks.filter(pk => pk.manual);
      const peaks = [...auto, ...manual].map((pk, k) => ({
        ...pk, color: PEAK_COLORS[k % PEAK_COLORS.length],
      }));
      return { ...s, peaks, defectResults: [], identifyError: null };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sgWindow, sgOrder]);
  // Mark a peak as THE bandgap (band-to-band) peak of its spectrum —
  // radio-style, one per spectrum: marking one unmarks the previous;
  // clicking the marked one unmarks it. Other peaks then read as defect
  // emission at depth dE = Eg_peak − peak (the physically meaningful
  // quantity for graded / ternary absorbers).
  function toggleEgPeak(spectrumId, peakIdx) {
    setSpectra(prev => prev.map(s => {
      if (s.id !== spectrumId) return s;
      return {
        ...s,
        peaks: s.peaks.map((pk, j) => ({
          ...pk,
          isEg: j === peakIdx ? !pk.isEg : false,
        })),
      };
    }));
  }
  function removePeak(spectrumId, peakIdx) {
    setSpectra(prev => prev.map(s => {
      if (s.id !== spectrumId) return s;
      const nextPeaks = s.peaks
        .filter((_, j) => j !== peakIdx)
        .map((pk, k) => ({ ...pk, color: PEAK_COLORS[k % PEAK_COLORS.length] }));
      const nextResults = s.defectResults.filter((_, j) => j !== peakIdx);
      return { ...s, peaks: nextPeaks, defectResults: nextResults };
    }));
  }
  // Shared add path for BOTH the type-in field and the click-to-add
  // preview: appends a manual peak carrying canonical nm/eV values.
  function addPeakAt(spectrumId, { nm, eV, y = 0 }) {
    setSpectra(prev => prev.map(s => {
      if (s.id !== spectrumId) return s;
      const idx = s.peaks.length;
      return {
        ...s,
        peaks: [...s.peaks, {
          x: s.meta.isWavelength ? nm : eV, y, nm, eV, manual: true,
          color: PEAK_COLORS[idx % PEAK_COLORS.length],
        }],
      };
    }));
  }
  function addManualPeak(spectrumId, valueText) {
    const v = parseFloat(valueText);
    if (!Number.isFinite(v) || v <= 0) return;
    const isWavelength = unitMode === 'nm';
    addPeakAt(spectrumId, {
      nm: isWavelength ? v : eVtoNm(v),
      eV: isWavelength ? nmToEV(v) : v,
    });
  }

  // ── AI identification (per spectrum) ────────────────────────────
  async function identifyAllPeaks(spectrumId) {
    const target = spectra.find(s => s.id === spectrumId);
    if (!target || target.peaks.length === 0) return;
    setSpectra(prev => prev.map(s =>
      s.id === spectrumId
        ? { ...s, isIdentifying: true, identifyError: null }
        : s));

    // User-marked bandgap context. The backend DefectRequest schema
    // (backend/app/api/routes/ai_analysis.py) has no dedicated bandgap
    // field and Pydantic silently DROPS unknown keys, so the reliable
    // channel is `notes`, which identify-defect interpolates verbatim
    // into the model prompt. user_bandgap_eV is sent too, so a future
    // backend field picks it up without a frontend change.
    const egPeak = target.peaks.find(pk => pk.isEg);
    const egNote = egPeak
      ? `User-marked bandgap (band-to-band) peak: ${egPeak.eV.toFixed(3)} eV. ` +
        'Treat sub-bandgap peaks as defect emission at depth ' +
        'dE = Eg_peak - peak_energy from the relevant band edge.'
      : '';
    const mergedNotes = [notes, egNote].filter(Boolean).join(' | ');

    const results = [];
    let firstError = null;
    for (let i = 0; i < target.peaks.length; i++) {
      const pk = target.peaks[i];
      try {
        const laser = parseFloat(laserNm);
        const { data } = await axios.post(`${API}/ai/identify-defect`, {
          peak_eV: pk.eV,
          peak_nm: pk.nm,
          material,
          temperature_K: tempK,
          absorber_thickness_um: thickness,
          notes: mergedNotes || null,
          laser_nm: Number.isFinite(laser) && laser > 0 ? laser : null,
          ...(egPeak ? { user_bandgap_eV: egPeak.eV } : {}),
        });
        results.push({ ...data, _editedNt: data.Nt_estimate });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        results.push({
          peak_eV: pk.eV, peak_nm: pk.nm,
          identity: 'Identification failed',
          Et_eV: 0, Et_reference: 'from_Ev',
          sigma_n: '1e-13', sigma_p: '1e-13', Nt_estimate: '1e14',
          spatial_location: '', citation: '',
          confidence: 'low', reasoning: detail || e.message || 'Network error',
          _editedNt: '1e14', _failed: true,
        });
        if (!firstError) firstError = detail || e.message;
      }
    }
    setSpectra(prev => prev.map(s =>
      s.id === spectrumId
        ? { ...s, isIdentifying: false, defectResults: results, identifyError: firstError }
        : s));
  }

  function updateNt(spectrumId, peakIdx, val) {
    setSpectra(prev => prev.map(s => {
      if (s.id !== spectrumId) return s;
      return {
        ...s,
        defectResults: s.defectResults.map((r, j) =>
          j === peakIdx ? { ...r, _editedNt: val } : r),
      };
    }));
  }

  function addToQueue(result) {
    const Nt = parseFloat(result._editedNt || result.Nt_estimate);
    const sigma_n = parseFloat(result.sigma_n);
    const sigma_p = parseFloat(result.sigma_p);
    if (!Number.isFinite(Nt) || !Number.isFinite(sigma_n) || !Number.isFinite(sigma_p)) {
      alert('Nt / sigma values are not numeric — cannot queue.');
      return;
    }
    // Use the AI-identified donor/acceptor character when the response
    // provides one; otherwise keep it null (= unknown) instead of
    // silently assuming an acceptor.
    const rawType = String(result.trap_type || '').toLowerCase();
    const trap_type = ['donor', 'acceptor', 'neutral'].includes(rawType)
      ? rawType : null;
    setDefectQueue(prev => [...prev, {
      name: result.identity,
      peak_eV: result.peak_eV,
      confidence: result.confidence || null,
      Et_eV: Number(result.Et_eV) || 0,
      energy_reference: result.Et_reference === 'from_Ec' ? 'from_Ec' : 'from_Ev',
      sigma_n, sigma_p, Nt,
      trap_type,
    }]);
  }

  function removeFromQueue(i) {
    setDefectQueue(prev => prev.filter((_, j) => j !== i));
  }

  function applyQueue() {
    if (!defectQueue.length || !onApplyDefects) return;
    // Emit the INTERNAL layer-trap shape ({type, Et, Et_ref, ...}) that
    // SimulatorPanel merges into layer.traps and serializeLayer reads —
    // the previous API-shaped keys (trap_type/Et_eV/energy_reference)
    // were silently dropped there (Et became 0, type became 'acceptor').
    // Unknown donor/acceptor character maps to 'neutral' (symmetric SRH
    // in the current backend) rather than assuming an acceptor.
    onApplyDefects(defectQueue.map(d => ({
      id:      Math.random().toString(36).slice(2),
      type:    d.trap_type || 'neutral',
      Et:      d.Et_eV,
      Et_ref:  d.energy_reference === 'from_Ec' ? 'Ec' : 'Ev',
      sigma_n: d.sigma_n,
      sigma_p: d.sigma_p,
      Nt:      d.Nt,
    })));
    setDefectQueue([]);
  }

  // ── Per-spectrum chart data ─────────────────────────────────────
  // Each spectrum gets its own array of {x, y} re-interpreted into the
  // chosen unit.  Recharts allows a per-Line `data` prop, so we let each
  // spectrum carry its own grid rather than forcing a join.
  const visibleSpectra = useMemo(() => spectra.filter(s => s.visible), [spectra]);

  const spectraChartData = useMemo(() =>
    visibleSpectra.map(s => {
      const sourceIsWavelength = s.meta?.isWavelength;
      const toUnit = (p) => ({
        x: unitMode === 'nm'
          ? (sourceIsWavelength ? p.x : eVtoNm(p.x))
          : (sourceIsWavelength ? nmToEV(p.x) : p.x),
        y: p.y,
      });
      const pts = s.raw.map(toUnit);
      pts.sort((a, b) => a.x - b.x);
      let smoothPts = null;
      if (sgWindow > 0) {
        // Smooth in the file's native x grid (≈uniform spacing), then
        // convert to the display unit.
        smoothPts = savitzkyGolay(s.raw, sgWindow, sgOrder).map(toUnit);
        smoothPts.sort((a, b) => a.x - b.x);
      }
      return { id: s.id, name: s.name, color: s.color, data: pts, smoothData: smoothPts };
    }), [visibleSpectra, unitMode, sgWindow, sgOrder]);

  // CSV: union of x values across all visible spectra, one column per spectrum
  const plCSVData = useMemo(() => {
    if (spectraChartData.length === 0) return [];
    const xs = new Set();
    spectraChartData.forEach(s => s.data.forEach(p => xs.add(p.x)));
    const sorted = Array.from(xs).sort((a, b) => a - b);
    const lookups = spectraChartData.map(s => {
      const m = new Map();
      s.data.forEach(p => m.set(p.x, p.y));
      return m;
    });
    return sorted.map(x => {
      const row = { [`x_${unitMode}`]: x };
      spectraChartData.forEach((s, i) => {
        const y = lookups[i].get(x);
        row[s.name] = y != null ? y : '';
      });
      return row;
    });
  }, [spectraChartData, unitMode]);

  // Flatten all peaks across visible spectra for ReferenceLines + legend.
  // Number them globally so the legend chips and vertical lines align.
  const allPeaks = useMemo(() =>
    visibleSpectra.flatMap((s, si) =>
      s.peaks.map((pk, pi) => ({
        ...pk,
        spectrumId: s.id,
        spectrumName: s.name,
        spectrumColor: s.color,
        peakIdx: pi,
        // Use the per-peak palette colour for consistent identity;
        // the spectrum colour is shown separately on the legend chip.
      }))
    ), [visibleSpectra]);

  // Per-spectrum user-marked bandgap peak (if any) — for ΔE readouts in
  // the legend and the AI request context.
  const egBySpectrum = useMemo(() => {
    const m = new Map();
    visibleSpectra.forEach(s => {
      const eg = s.peaks.find(pk => pk.isEg);
      if (eg) m.set(s.id, eg);
    });
    return m;
  }, [visibleSpectra]);

  // Full data x-extent (display units) — the label-stagger fallback
  // domain when no zoom window is active.
  const xDataExtent = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    spectraChartData.forEach(s => s.data.forEach(p => {
      if (p.x < lo) lo = p.x;
      if (p.x > hi) hi = p.x;
    }));
    return hi > lo ? [lo, hi] : null;
  }, [spectraChartData]);

  // Staggered vertical slots for the inline "Eg" labels (one per marked
  // spectrum) so they don't collide when several spectra mark nearby
  // bandgap peaks. Keyed by `${spectrumId}-${peakIdx}`.
  const egLabelSlots = useMemo(() => {
    const egs = allPeaks.filter(pk => pk.isEg);
    const xs = egs.map(pk => (unitMode === 'nm' ? pk.nm : pk.eV));
    const slots = assignLabelSlots(xs, zoomDomain || xDataExtent);
    const m = new Map();
    egs.forEach((pk, i) => m.set(`${pk.spectrumId}-${pk.peakIdx}`, slots[i]));
    return m;
  }, [allPeaks, unitMode, zoomDomain, xDataExtent]);

  // Target for manual / click-to-add peaks: the expanded spectrum, else
  // the most recently added one (same rule ManualPeakAdd always used).
  const targetSpectrum =
    spectra.find(s => s.isExpanded) || spectra[spectra.length - 1] || null;

  // Snap a clicked display-unit x to the nearest data point of the target
  // spectrum's DISPLAYED trace (SG-smoothed when smoothing is on).
  function placePreview(xDisplay) {
    if (!targetSpectrum || !Number.isFinite(xDisplay)) return;
    const s = spectraChartData.find(d => d.id === targetSpectrum.id);
    const pts = s ? (sgWindow > 0 && s.smoothData ? s.smoothData : s.data) : null;
    if (!pts || pts.length === 0) return;
    let best = pts[0];
    for (const p of pts) {
      if (Math.abs(p.x - xDisplay) < Math.abs(best.x - xDisplay)) best = p;
    }
    setPeakPreview({
      nm: unitMode === 'nm' ? best.x : eVtoNm(best.x),
      eV: unitMode === 'nm' ? nmToEV(best.x) : best.x,
      y: best.y,
    });
  }

  function confirmPreview() {
    if (!peakPreview || !targetSpectrum) return;
    addPeakAt(targetSpectrum.id, peakPreview);
    setPeakPreview(null);
  }

  // Enter → add previewed peak; Escape → dismiss. Skipped while typing in
  // any form control so the type-in add field keeps its own Enter.
  useEffect(() => {
    if (!peakPreview) return undefined;
    function onKey(e) {
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag.toUpperCase())) return;
      if (e.key === 'Enter') { e.preventDefault(); confirmPreview(); }
      else if (e.key === 'Escape') setPeakPreview(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peakPreview, targetSpectrum, unitMode]);

  // Vertical autoscale: zoomed into an x window → Y spans just the data
  // visible in that window (displayed traces — smoothed when SG is on —
  // across all visible spectra) instead of the global [0, 1.05].
  const yDomain = useMemo(() => {
    if (!zoomDomain) return [0, 1.05];
    const [lo, hi] = zoomDomain;
    let min = Infinity, max = -Infinity;
    spectraChartData.forEach(s => {
      const pts = sgWindow > 0 && s.smoothData ? s.smoothData : s.data;
      pts.forEach(p => {
        if (p.x >= lo && p.x <= hi) {
          if (p.y < min) min = p.y;
          if (p.y > max) max = p.y;
        }
      });
    });
    if (!(max > -Infinity) || !(max > 0)) return [0, 1.05];
    return [min > 0 ? min * 0.95 : 0, max * 1.05];
  }, [zoomDomain, spectraChartData, sgWindow]);

  const previewLabel = peakPreview
    ? (unitMode === 'nm'
        ? `${peakPreview.nm.toFixed(1)} nm`
        : `${peakPreview.eV.toFixed(3)} eV`)
    : '';

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '220px 1fr 320px',
      // Pin the row track to the parent's height. Without an explicit
      // grid-template-rows, the row sizes to content — making each
      // column's `height: 100%` resolve to the row's content-sized
      // (overgrown) track and breaking inner overflow-y: auto. The
      // `minmax(0, 1fr)` lets the row shrink below content height, which
      // is exactly what enables children to engage their own scrollbars.
      // See commit message + frontend/docs/persistent_ai_architecture_design.md
      // for the diagnostic background.
      gridTemplateRows: 'minmax(0, 1fr)',
      height: '100%', width: '100%', background: c.bg,
      fontFamily: 'IBM Plex Mono, monospace',
      overflow: 'hidden',
    }}>

      {/* ── LEFT: Device context ───────────────────────────────── */}
      <div style={{
        borderRight: `1px solid ${c.border}`, padding: 16,
        overflowY: 'auto', height: '100%',
      }}>
        <SectionLabel c={c}>Sample Definition</SectionLabel>

        <Field c={c} label="Material">
          <select value={material} onChange={e => setMaterial(e.target.value)}
            style={inputStyle(c)}>
            {['CdTe','CdSeTe','CIGS','Perovskite','Silicon','Other'].map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </Field>

        <Field c={c} label="Thickness (μm)">
          <input type="number" value={thickness} step={0.1} min={0.01}
            onChange={e => setThickness(parseFloat(e.target.value) || 0)}
            style={inputStyle(c)} />
        </Field>

        <Field c={c} label="Temperature (K)">
          <input type="number" value={tempK} step={1} min={4} max={500}
            onChange={e => setTempK(parseFloat(e.target.value) || 300)}
            style={inputStyle(c)} />
        </Field>

        <Field c={c} label="Excitation Laser (nm)">
          <input type="number" value={laserNm} step={1} min={200} max={2000}
            onChange={e => setLaserNm(e.target.value)}
            placeholder="e.g. 532"
            style={inputStyle(c)} />
          <div style={{ fontSize: 10, color: c.textMuted, marginTop: 2 }}>
            Common: 405, 445, 532, 633, 785 nm. Peaks within ±10&nbsp;nm
            of this are filtered as laser scatter.
          </div>
        </Field>

        <Field c={c} label="Notes">
          <textarea rows={4} value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Sample ID, processing conditions..."
            style={{ ...inputStyle(c), resize: 'vertical', fontSize: 11,
                      fontFamily: 'IBM Plex Mono, monospace' }} />
        </Field>

        <div style={{
          marginTop: 16, paddingTop: 14,
          borderTop: `1px solid ${c.border}`,
        }}>
          <SectionLabel c={c}>AI Assistance</SectionLabel>
          <div style={{ fontSize: 11, color: c.textMuted, lineHeight: 1.5 }}>
            Device context helps the AI identify defects accurately.
            For example, sub-bandgap emission in CdSeTe/CdTe is likely
            from the Se-rich front region.
          </div>
        </div>
      </div>

      {/* ── CENTER: Data + visualization ───────────────────────── */}
      {/* overflowY: auto (was overflow: hidden) — every ancestor up
       *  through .main-layout hides overflow, so during analysis the
       *  content below the chart (legend, defect cards, manual-peak
       *  row) grew past the viewport with no way to scroll to it. The
       *  chart keeps its fixed 420px flex-basis, so drag-zoom mouse
       *  handling is unaffected; the scrollbar only appears when the
       *  column content actually exceeds the tab height. */}
      <div style={{
        padding: 20, display: 'flex', flexDirection: 'column',
        minHeight: 0, height: '100%',
        overflowY: 'auto', overflowX: 'hidden',
      }}>
        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
          {['pl','trpl'].map(t => (
            <button key={t} onClick={() => setSubTab(t)}
              style={{
                padding: '5px 14px', cursor: 'pointer',
                background: subTab === t ? c.btnActive : c.btnInactive,
                color:      subTab === t ? c.btnPrimaryText : c.btnInactiveText,
                border: `1px solid ${subTab === t ? c.btnActive : c.border}`,
                borderRadius: 4,
                fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
              }}>
              {t === 'pl' ? 'PL Spectrum' : 'TRPL'}
            </button>
          ))}
        </div>

        {subTab === 'pl' && (
          <>
            {/* Upload area: prominent only when no spectra yet */}
            {spectra.length === 0 && (
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleFile(f);
                }}
                style={{
                  border: `2px dashed ${c.border}`,
                  borderRadius: 8, padding: 18,
                  textAlign: 'center', cursor: 'pointer',
                  marginBottom: 10, background: c.bgSecondary,
                }}>
                <div style={{ fontSize: 14, color: c.text, marginBottom: 4 }}>
                  📂 Upload PL Spectrum
                </div>
                <div style={{ fontSize: 11, color: c.textMuted }}>
                  CSV / TXT — 2 columns: wavelength (nm) or energy (eV), intensity.
                  Unit is auto-detected (x &gt; 100 → nm) and can be overridden
                  per file after upload.
                </div>
                <input ref={fileRef} type="file" accept=".csv,.txt,.dat,.tsv"
                  hidden onChange={e => handleFile(e.target.files?.[0])} />
              </div>
            )}

            {/* Spectra list (visible whenever ≥1 spectrum) */}
            {spectra.length > 0 && (
              <div style={{
                marginBottom: 10, padding: 8,
                background: c.bgSecondary, borderRadius: 6,
                border: `1px solid ${c.border}`,
              }}>
                <div style={{
                  fontSize: 11, color: c.textMuted, marginBottom: 6,
                  letterSpacing: 1, textTransform: 'uppercase',
                }}>
                  Loaded Spectra ({spectra.length})
                </div>
                {spectra.map(s => (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '3px 0', fontSize: 12, color: c.text,
                  }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: s.color, flexShrink: 0,
                    }} />
                    <span style={{
                      flex: 1, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      opacity: s.visible ? 1 : 0.5,
                    }}>
                      {s.name} <span style={{ color: c.textMuted, fontSize: 10 }}>
                        — {s.peaks.length} peaks
                      </span>
                    </span>
                    <select
                      value={s.meta.unitChoice || 'auto'}
                      onChange={e => setSpectrumUnit(s.id, e.target.value)}
                      title="X-axis unit of this file (auto-detected from the x range; override if wrong)"
                      style={{
                        padding: '1px 4px', fontSize: 10,
                        background: c.input, border: `1px solid ${c.border}`,
                        color: c.inputText, borderRadius: 3,
                        fontFamily: 'IBM Plex Mono, monospace',
                        cursor: 'pointer', flexShrink: 0,
                      }}>
                      <option value="auto">
                        Auto (detected: {s.meta.detectedIsWavelength ? 'nm' : 'eV'})
                      </option>
                      <option value="nm">nm</option>
                      <option value="eV">eV</option>
                    </select>
                    <button onClick={() => toggleVisible(s.id)}
                      title={s.visible ? 'Hide' : 'Show'}
                      style={iconBtnStyle(c)}>
                      {s.visible ? '👁' : '⊘'}
                    </button>
                    <button onClick={() => removeSpectrum(s.id)}
                      title="Remove"
                      style={{ ...iconBtnStyle(c), color: c.red }}>
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addSpectrumInputRef.current?.click()}
                  style={{
                    width: '100%', padding: '4px',
                    marginTop: 6, background: 'transparent',
                    border: `1px dashed ${c.border}`, borderRadius: 4,
                    color: c.textMuted, cursor: 'pointer', fontSize: 12,
                    fontFamily: 'IBM Plex Mono, monospace',
                  }}>
                  + Add Spectrum
                </button>
                <input ref={addSpectrumInputRef} type="file"
                  accept=".csv,.txt,.dat,.tsv" hidden
                  onChange={e => {
                    handleFile(e.target.files?.[0]);
                    e.target.value = '';
                  }} />
              </div>
            )}

            {plError && (
              <div style={{ color: c.red, fontSize: 12, marginBottom: 8 }}>{plError}</div>
            )}

            {/* Unit selector */}
            {spectra.length > 0 && (
              <div style={{
                display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8,
                fontSize: 11, color: c.textMuted, flexWrap: 'wrap',
              }}>
                <span>X axis:</span>
                {[['nm','Wavelength (nm)'],['eV','Energy (eV)']].map(([v, lbl]) => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="radio" name="unit" checked={unitMode === v}
                      onChange={() => setViewUnit(v)} />
                    {lbl}
                  </label>
                ))}
                <span style={{ marginLeft: 8 }}>Smoothing:</span>
                <select value={sgWindow}
                  onChange={e => setSgWindow(parseInt(e.target.value, 10))}
                  title="Savitzky-Golay smoothing — applied before peak detection"
                  style={{
                    padding: '2px 5px', fontSize: 11,
                    background: c.input, border: `1px solid ${c.border}`,
                    color: c.inputText, borderRadius: 3,
                    fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer',
                  }}>
                  <option value={0}>Off</option>
                  {SG_WINDOWS.map(w => (
                    <option key={w} value={w}>SG, window {w}</option>
                  ))}
                </select>
                {sgWindow > 0 && (
                  <select value={sgOrder}
                    onChange={e => setSgOrder(parseInt(e.target.value, 10))}
                    title="Savitzky-Golay polynomial order"
                    style={{
                      padding: '2px 5px', fontSize: 11,
                      background: c.input, border: `1px solid ${c.border}`,
                      color: c.inputText, borderRadius: 3,
                      fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer',
                    }}>
                    <option value={2}>order 2</option>
                    <option value={3}>order 3</option>
                  </select>
                )}
                <span style={{
                  marginLeft: 'auto', fontSize: 10, color: c.textMuted,
                  fontStyle: 'italic',
                }}>
                  Click plot to preview a peak · drag to zoom ·
                  double-click to reset
                </span>
                <button onClick={clearAll} style={{
                  padding: '3px 10px', fontSize: 11,
                  background: c.btnSecondary, color: c.btnSecondaryText,
                  border: `1px solid ${c.border}`, borderRadius: 4,
                  cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace',
                }}>Clear All</button>
              </div>
            )}

            {/* Chart — taller (420 px). Double-click the chart to reset zoom. */}
            <div id={PL_CHART_ID}
              onDoubleClick={resetZoom}
              style={{
                flex: '0 0 auto', height: 420, marginBottom: 10,
                position: 'relative',
                cursor: dragRange.dragging ? 'ew-resize' : (zoomDomain ? 'zoom-out' : 'crosshair'),
              }}>
              {/* Reset Zoom button — overlays the chart, only shown when zoomed. */}
              {zoomDomain && (
                <button onClick={resetZoom}
                  title="Reset zoom (or double-click chart)"
                  style={{
                    position: 'absolute', top: 6, right: 10, zIndex: 5,
                    padding: '3px 10px', fontSize: 10,
                    background: c.btnSecondary, color: c.btnSecondaryText,
                    border: `1px solid ${c.border}`, borderRadius: 4,
                    cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace',
                    letterSpacing: 0.5,
                  }}>
                  ⤺ Reset Zoom
                </button>
              )}
              {spectraChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart margin={{ top: 30, right: 30, bottom: 36, left: 24 }}
                    onMouseDown={onChartMouseDown}
                    onMouseMove={onChartMouseMove}
                    onMouseUp={() => onChartMouseUp(false)}
                    onMouseLeave={() => onChartMouseUp(true)}>
                    <CartesianGrid strokeDasharray="3 3" stroke={c.plotGrid} />
                    <XAxis dataKey="x" type="number"
                      domain={zoomDomain || ['auto','auto']}
                      allowDataOverflow={!!zoomDomain}
                      allowDuplicatedCategory={false}
                      tickFormatter={v => unitMode === 'nm'
                        ? String(Math.round(v)) : Number(v).toFixed(3)}
                      tick={{ fill: c.plotText, fontSize: 11, fontFamily: 'IBM Plex Mono' }}
                      label={{ value: unitMode === 'nm' ? 'Wavelength (nm)' : 'Energy (eV)',
                        position: 'insideBottom', offset: -10,
                        fill: c.plotText, fontSize: 12, fontFamily: 'IBM Plex Mono' }} />
                    <YAxis dataKey="y" domain={yDomain}
                      allowDataOverflow={!!zoomDomain}
                      tickFormatter={v => Number(v).toFixed(2)}
                      tick={{ fill: c.plotText, fontSize: 11, fontFamily: 'IBM Plex Mono' }}
                      label={{ value: 'Normalized PL', angle: -90,
                        position: 'insideLeft', offset: -4, textAnchor: 'middle',
                        fill: c.plotText, fontSize: 12, fontFamily: 'IBM Plex Mono' }} />
                    <Tooltip contentStyle={{ background: c.bgCard, border: `1px solid ${c.border}` }} />
                    {/* Per-spectrum line(s). With SG smoothing on: raw data
                        faint underneath, smoothed curve solid on top. */}
                    {sgWindow > 0 && spectraChartData.map(s => (
                      <Line key={`raw-${s.id}`} data={s.data}
                        dataKey="y" stroke={s.color} strokeOpacity={0.3}
                        strokeWidth={1} dot={false} name={`${s.name} (raw)`}
                        isAnimationActive={false} />
                    ))}
                    {spectraChartData.map(s => (
                      <Line key={s.id} data={sgWindow > 0 ? s.smoothData : s.data}
                        dataKey="y" stroke={s.color}
                        strokeWidth={1.8} dot={false}
                        name={sgWindow > 0 ? `${s.name} (SG)` : s.name}
                        isAnimationActive={false} />
                    ))}
                    {/* Excitation laser line */}
                    {(() => {
                      const laser = parseFloat(laserNm);
                      if (!Number.isFinite(laser) || laser <= 0) return null;
                      const xVal = unitMode === 'nm' ? laser : nmToEV(laser);
                      return (
                        <ReferenceLine x={xVal} stroke={c.textMuted}
                          strokeDasharray="2 4" strokeWidth={1.5}
                          label={{ value: `🚫 Excitation (${laser} nm)`,
                            position: 'insideTopLeft',
                            fill: c.textMuted, fontSize: 10, dy: 6 }} />
                      );
                    })()}
                    {/* Vertical peak markers — NO inline text labels.
                        The legend below the plot carries the identity.
                        ReferenceLine positions are tied to x VALUES, so
                        they stay anchored to the correct peak position
                        when the x-axis domain is zoomed. */}
                    {allPeaks.map((pk, i) => {
                      const xVal = unitMode === 'nm' ? pk.nm : pk.eV;
                      // User-marked bandgap peak: gold, thicker, "Eg" tag —
                      // the one deliberate exception to no-inline-labels.
                      if (pk.isEg) {
                        // Vertical slot from the greedy stagger so several
                        // marked-Eg labels never sit on top of each other.
                        const slot = egLabelSlots.get(
                          `${pk.spectrumId}-${pk.peakIdx}`) || 0;
                        return (
                          <ReferenceLine
                            key={`${pk.spectrumId}-${pk.peakIdx}`}
                            x={xVal} stroke="#ffd700" strokeWidth={2.5}
                            strokeDasharray="6 3"
                            label={{ value: 'Eg', position: 'top',
                              dy: slot * 13,
                              fill: '#ffd700', fontSize: 11,
                              fontFamily: 'IBM Plex Mono' }} />
                        );
                      }
                      return (
                        <ReferenceLine
                          key={`${pk.spectrumId}-${pk.peakIdx}`}
                          x={xVal} stroke={pk.color} strokeDasharray="4 3" />
                      );
                    })}
                    {/* Click-to-add preview — white dashed line at the
                        snapped x, value shown beside it. Confirm via the
                        "Add peak here" button or Enter. */}
                    {peakPreview && (
                      <ReferenceLine
                        x={unitMode === 'nm' ? peakPreview.nm : peakPreview.eV}
                        stroke="#ffffff" strokeDasharray="6 4" strokeWidth={1.5}
                        label={{ value: previewLabel, position: 'top',
                          fill: '#ffffff', fontSize: 11,
                          fontFamily: 'IBM Plex Mono' }} />
                    )}
                    {/* Drag-zoom selection rectangle while the user is
                        choosing a range. Disappears on mouse-up. */}
                    {dragRange.dragging && dragRange.start != null && dragRange.end != null && (
                      <ReferenceArea
                        x1={Math.min(dragRange.start, dragRange.end)}
                        x2={Math.max(dragRange.start, dragRange.end)}
                        strokeOpacity={0.4}
                        stroke={c.accent}
                        fill={c.accent}
                        fillOpacity={0.12}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{
                  height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  color: c.textMuted, fontSize: 12,
                }}>
                  Upload a PL spectrum to begin.
                </div>
              )}
            </div>

            {/* Export bar — directly below the plot */}
            {spectraChartData.length > 0 && (
              <div style={{
                display: 'flex', gap: 6, justifyContent: 'flex-end',
                padding: '4px 0 8px',
                borderBottom: `1px solid ${c.border}`,
                marginBottom: 10,
              }}>
                <button
                  onClick={() => exportCSV(plCSVData, 'PL_spectrum')}
                  disabled={plCSVData.length === 0}
                  style={miniBtnStyle(c, plCSVData.length > 0)}>
                  📊 CSV
                </button>
                <button
                  onClick={() => exportPNG(PL_CHART_ID, 'PL_spectrum')}
                  style={miniBtnStyle(c, true)}>
                  🖼 PNG
                </button>
                <button
                  onClick={() => exportSVG(PL_CHART_ID, 'PL_spectrum')}
                  style={miniBtnStyle(c, true)}>
                  📈 SVG
                </button>
              </div>
            )}

            {/* Peak legend below plot — chips with colour, x value, removable */}
            {allPeaks.length > 0 && sgWindow > 0 && (
              <div style={{
                fontSize: 10, color: c.textMuted, padding: '4px 0 0',
                fontStyle: 'italic',
              }}>
                peaks from SG-smoothed data, window {sgWindow}, order {sgOrder}
              </div>
            )}
            {allPeaks.length > 0 && (
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6,
                padding: '6px 0', marginBottom: 8,
              }}>
                {allPeaks.map((pk, i) => {
                  const eg = egBySpectrum.get(pk.spectrumId);
                  const dMeV = (!pk.isEg && eg)
                    ? (eg.eV - pk.eV) * 1000 : null;
                  return (
                  <div key={`leg-${pk.spectrumId}-${pk.peakIdx}`} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    fontSize: 11, padding: '2px 8px',
                    background: c.bgSecondary, borderRadius: 4,
                    border: pk.isEg
                      ? '2px solid #ffd700' : `1px solid ${pk.color}`,
                    color: c.text,
                  }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: pk.isEg ? '#ffd700' : pk.color,
                    }} />
                    {pk.isEg && (
                      <span style={{
                        background: '#ffd700', color: '#000',
                        fontSize: 9, fontWeight: 700, padding: '0 4px',
                        borderRadius: 3, letterSpacing: 0.5,
                      }}>Eg</span>
                    )}
                    <span>{pk.nm.toFixed(0)} nm</span>
                    <span style={{ color: c.textMuted }}>
                      ({pk.eV.toFixed(3)} eV)
                    </span>
                    {dMeV != null && (
                      <span
                        title="Offset from the marked bandgap peak — the defect depth ΔE = Eg_peak − peak"
                        style={{ color: '#ffd700', fontSize: 10 }}>
                        ΔE {Math.round(dMeV)} meV
                      </span>
                    )}
                    {visibleSpectra.length > 1 && (
                      <span style={{
                        color: pk.spectrumColor, fontSize: 10,
                      }}>· {truncate(pk.spectrumName, 18)}</span>
                    )}
                    {pk.manual && (
                      <span style={{ color: c.textMuted, fontSize: 9 }}>·M</span>
                    )}
                    <button
                      onClick={() => toggleEgPeak(pk.spectrumId, pk.peakIdx)}
                      title={pk.isEg
                        ? 'Unmark as bandgap peak'
                        : 'Mark as the bandgap (band-to-band) peak of this spectrum'}
                      style={{
                        background: 'none',
                        border: `1px solid ${pk.isEg ? '#ffd700' : c.border}`,
                        color: pk.isEg ? '#ffd700' : c.textMuted,
                        cursor: 'pointer', fontSize: 9, padding: '0 4px',
                        borderRadius: 3, lineHeight: 1.4,
                        fontFamily: 'IBM Plex Mono, monospace',
                      }}>Eg</button>
                    <button
                      onClick={() => removePeak(pk.spectrumId, pk.peakIdx)}
                      title="Remove peak"
                      style={{
                        background: 'none', border: 'none',
                        color: c.textMuted, cursor: 'pointer',
                        fontSize: 11, padding: 0, lineHeight: 1,
                      }}>×</button>
                  </div>
                  );
                })}
              </div>
            )}

            {/* Click-to-add preview confirm bar */}
            {peakPreview && targetSpectrum && (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'center',
                fontSize: 12, color: c.text, marginBottom: 6,
                padding: '4px 8px', background: c.bgSecondary,
                border: `1px dashed ${c.border}`, borderRadius: 4,
              }}>
                <span>
                  Preview: <b>{previewLabel}</b>
                  <span style={{ color: c.textMuted, marginLeft: 4 }}>
                    ({unitMode === 'nm'
                      ? `${peakPreview.eV.toFixed(3)} eV`
                      : `${peakPreview.nm.toFixed(1)} nm`})
                  </span>
                </span>
                <button onClick={confirmPreview} style={{
                  padding: '3px 10px', background: c.btnPrimary,
                  color: c.btnPrimaryText, border: 'none', borderRadius: 4,
                  cursor: 'pointer', fontSize: 11,
                  fontFamily: 'IBM Plex Mono, monospace',
                }}>+ Add peak here (Enter)</button>
                <button onClick={() => setPeakPreview(null)}
                  title="Dismiss preview (Esc)"
                  style={{
                    padding: '3px 8px', background: c.btnSecondary,
                    color: c.btnSecondaryText, border: `1px solid ${c.border}`,
                    borderRadius: 4, cursor: 'pointer', fontSize: 11,
                    fontFamily: 'IBM Plex Mono, monospace',
                  }}>✕ Esc</button>
              </div>
            )}

            {/* Manual peak add — operates on the most-recently-expanded
                spectrum (or the only spectrum when there is one). */}
            {targetSpectrum && (
              <ManualPeakAdd
                c={c} unitMode={unitMode}
                targetName={targetSpectrum.name}
                showTarget={spectra.length > 1}
                onAdd={text => addManualPeak(targetSpectrum.id, text)} />
            )}

            {/* Band-diagram schematic of the ACTIVE DEVICE (same as the
                PL/TRPL tab): absorber bandgap + its defect levels,
                shallow/deep zones — no peak-marking required. */}
            {deviceEg != null && (
              <BandgapDefectSchematic
                Eg={deviceEg}
                defects={deviceDefects}
                c={c}
                title="Absorber bandgap & active defect levels"
              />
            )}

            {/* Band-diagram schematic — anchored to the user-marked Eg
                peak; shows queued defects at their Et positions. */}
            {(() => {
              const eg = [...egBySpectrum.values()][0];
              if (!eg) {
                return defectQueue.length > 0 ? (
                  <div style={{
                    marginTop: 10, fontSize: 11, color: c.textMuted,
                    fontStyle: 'italic',
                  }}>
                    Mark a peak as the bandgap (Eg) to see the band-diagram
                    schematic of the queued defects.
                  </div>
                ) : null;
              }
              return (
                <BandgapDefectSchematic
                  Eg={eg.eV}
                  defects={defectQueue.map((d, i) => ({
                    id: i,
                    name: d.name,
                    Et_eV: d.Et_eV,
                    reference: d.energy_reference,
                  }))}
                  c={c}
                  title="Bandgap & queued defect levels"
                />
              );
            })()}
          </>
        )}

        {subTab === 'trpl' && (
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: c.textMuted, fontSize: 13, padding: 20, textAlign: 'center',
          }}>
            Use the existing <b>PL / TRPL</b> tab for TRPL upload, manual fit
            sliders, χ² scoring, and auto-fit.
          </div>
        )}
      </div>

      {/* ── RIGHT: AI defect identification, scrollable ─────────── */}
      {/* Third attempt at making this scroll. Previous attempts relied
       *  on the height chain (height: 100% → row track → grid parent)
       *  resolving to a definite value; in practice it does not, in a
       *  way I cannot pin down without browser dev tools. So this
       *  version takes self-contained ownership:
       *  - explicit calc'd height (no percentage, no inheritance)
       *  - no flex layout (Chromium has known overflow-in-flex-column
       *    quirks when the column's height comes from a chain)
       *  Children are block-level (SectionLabel + spectra list + queue
       *  block) so default block stacking handles vertical layout.
       *  If THIS doesn't scroll, the panel needs a real restructure;
       *  see backend/docs/questions_for_next_session.md (if present). */}
      <div style={{
        borderLeft: `1px solid ${c.border}`, padding: 16,
        overflowY: 'auto',
        height: 'calc(100vh - 56px - 40px)',
        boxSizing: 'border-box',
      }}>
        <SectionLabel c={c}>Defect Identification</SectionLabel>

        {spectra.length === 0 ? (
          <div style={{
            color: c.textMuted, fontSize: 12,
            textAlign: 'center', padding: '20px 8px', lineHeight: 1.5,
          }}>
            Upload a PL spectrum and detected peaks will appear here for
            AI-assisted defect identification.
          </div>
        ) : (
          spectra.map(spectrum => (
            <SpectrumDefectSection
              key={spectrum.id} spectrum={spectrum} c={c}
              onToggleExpand={() => toggleExpanded(spectrum.id)}
              onIdentify={() => identifyAllPeaks(spectrum.id)}
              onUpdateNt={(peakIdx, val) => updateNt(spectrum.id, peakIdx, val)}
              onAddToQueue={addToQueue}
            />
          ))
        )}

        {defectQueue.length > 0 && (
          <div style={{
            marginTop: 14, padding: 12,
            background: c.bgSecondary, borderRadius: 8,
            border: `1px solid ${c.border}`,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 12, color: c.text }}>
              PENDING DEFECTS ({defectQueue.length})
            </div>
            {defectQueue.map((d, i) => (
              <div key={i} style={{
                fontSize: 11, marginBottom: 4, color: c.text,
                display: 'flex', justifyContent: 'space-between', gap: 6,
              }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  • {d.name}: Et={d.Et_eV} eV, Nt={d.Nt.toExponential(1)},
                  type: {d.trap_type || 'unknown (applied as neutral)'}
                </span>
                <button onClick={() => removeFromQueue(i)}
                  style={{
                    background: 'none', border: 'none', color: c.red,
                    cursor: 'pointer', fontSize: 12, padding: 0,
                  }}>×</button>
              </div>
            ))}
            <button onClick={applyQueue} style={{
              width: '100%', marginTop: 8, padding: '6px 12px',
              background: theme === 'dark' ? '#1a472a' : '#dcfce7',
              color: theme === 'dark' ? '#00ff88' : '#15803d',
              border: `1px solid ${theme === 'dark' ? '#00ff88' : '#15803d'}`,
              borderRadius: 6, cursor: 'pointer',
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
            }}>
              → Add to Simulation &amp; Re-run
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Subcomponents / helpers ───────────────────────────────────────────────
function SectionLabel({ children, c }) {
  return (
    <div style={{
      fontSize: 11, letterSpacing: '2px',
      textTransform: 'uppercase', color: c.textMuted,
      marginBottom: 10, fontFamily: 'IBM Plex Mono, monospace',
    }}>{children}</div>
  );
}

function Field({ label, children, c }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{
        display: 'block', fontSize: 11, color: c.textMuted,
        marginBottom: 3, fontFamily: 'IBM Plex Mono, monospace',
      }}>{label}</label>
      {children}
    </div>
  );
}

function inputStyle(c) {
  return {
    width: '100%', boxSizing: 'border-box',
    padding: '5px 8px', fontSize: 12,
    background: c.input, border: `1px solid ${c.border}`,
    color: c.inputText, borderRadius: 4,
    fontFamily: 'IBM Plex Mono, monospace',
  };
}

function iconBtnStyle(c) {
  return {
    background: 'none', border: 'none', color: c.textMuted,
    cursor: 'pointer', fontSize: 12, padding: '0 4px',
    fontFamily: 'IBM Plex Mono, monospace',
  };
}

function miniBtnStyle(c, active) {
  return {
    padding: '3px 10px', fontSize: 11,
    background: c.btnSecondary, color: c.btnSecondaryText,
    border: `1px solid ${c.border}`, borderRadius: 4,
    cursor: active ? 'pointer' : 'not-allowed',
    opacity: active ? 1 : 0.45,
    fontFamily: 'IBM Plex Mono, monospace',
  };
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function ManualPeakAdd({ c, unitMode, targetName, showTarget, onAdd }) {
  const [text, setText] = useState('');
  const submit = () => {
    if (!text.trim()) return;
    onAdd(text);
    setText('');
  };
  return (
    <div style={{
      display: 'flex', gap: 6, alignItems: 'center',
      fontSize: 12, color: c.textMuted, flexWrap: 'wrap',
    }}>
      <input type="text" placeholder={`Add peak ${unitMode}...`}
        value={text} onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') submit(); }}
        style={{ ...inputStyle(c), width: 120 }} />
      <button onClick={submit} style={{
        padding: '4px 10px', background: c.btnSecondary,
        color: c.btnSecondaryText, border: `1px solid ${c.border}`,
        borderRadius: 4, cursor: 'pointer', fontSize: 12,
        fontFamily: 'IBM Plex Mono, monospace',
      }}>+ Add Peak</button>
      {showTarget && (
        <span style={{ fontSize: 11 }}>
          → adds to <b>{truncate(targetName, 24)}</b>
        </span>
      )}
    </div>
  );
}

function SpectrumDefectSection({
  spectrum, c, onToggleExpand, onIdentify, onUpdateNt, onAddToQueue,
}) {
  const peaksCount = spectrum.peaks.length;
  return (
    <div style={{
      border: `1px solid ${c.border}`, borderRadius: 8,
      marginBottom: 8, overflow: 'hidden',
    }}>
      {/* Header — always visible, click to expand */}
      <div onClick={onToggleExpand} style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', background: c.bgSecondary,
        cursor: 'pointer', userSelect: 'none',
      }}>
        <span style={{ fontSize: 11, color: c.textMuted, width: 12 }}>
          {spectrum.isExpanded ? '▼' : '►'}
        </span>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: spectrum.color, flexShrink: 0,
        }} />
        <span style={{
          flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600,
          color: c.text, overflow: 'hidden',
          whiteSpace: 'nowrap', textOverflow: 'ellipsis',
        }}>
          {spectrum.name}
        </span>
        <button
          onClick={e => { e.stopPropagation(); onIdentify(); }}
          disabled={spectrum.isIdentifying || peaksCount === 0}
          style={{
            padding: '3px 10px', fontSize: 11,
            background: spectrum.isIdentifying || peaksCount === 0
              ? c.btnInactive : c.btnPrimary,
            color: spectrum.isIdentifying || peaksCount === 0
              ? c.btnInactiveText : c.btnPrimaryText,
            border: 'none', borderRadius: 4,
            cursor: spectrum.isIdentifying || peaksCount === 0
              ? 'not-allowed' : 'pointer',
            fontFamily: 'IBM Plex Mono, monospace',
          }}>
          {spectrum.isIdentifying ? '🔍...' : `🔍 ID ${peaksCount}`}
        </button>
      </div>

      {spectrum.isExpanded && (
        <div style={{ padding: '8px 12px' }}>
          {spectrum.identifyError && spectrum.defectResults.length === 0 && (
            <div style={{ fontSize: 11, color: c.red, marginBottom: 8 }}>
              {spectrum.identifyError}
            </div>
          )}
          {spectrum.defectResults.length === 0 ? (
            <div style={{
              color: c.textMuted, fontSize: 12,
              textAlign: 'center', padding: '12px 0',
            }}>
              {peaksCount === 0
                ? 'No peaks detected'
                : `${peaksCount} peaks detected — click 🔍 ID to analyze`}
            </div>
          ) : (
            spectrum.defectResults.map((r, i) => (
              <DefectCard key={i} result={r} idx={i}
                onUpdateNt={onUpdateNt}
                onAddToQueue={() => onAddToQueue(r)}
                c={c} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function DefectCard({ result, idx, onUpdateNt, onAddToQueue, c }) {
  const failed = result._failed;
  const conf = String(result.confidence || '').toLowerCase();
  const confColor = conf === 'high' ? c.green
                  : conf === 'low'  ? c.red
                  : c.amber;
  return (
    <div style={{
      background: c.bgSecondary, border: `1px solid ${c.border}`,
      borderRadius: 8, padding: 10, marginBottom: 8, fontSize: 12,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 6,
      }}>
        <div style={{ fontWeight: 600, color: c.text }}>
          Peak at {Number(result.peak_eV).toFixed(3)} eV
          ({result.peak_nm != null ? `${Number(result.peak_nm).toFixed(0)} nm` : ''})
        </div>
        {conf && (
          <span title="AI self-reported confidence in this identification"
            style={{
              fontSize: 9, padding: '1px 8px', borderRadius: 10,
              border: `1px solid ${confColor}`, color: confColor,
              textTransform: 'uppercase', letterSpacing: 1,
              whiteSpace: 'nowrap', flexShrink: 0,
            }}>
            {conf} confidence
          </span>
        )}
      </div>
      <div style={{
        color: failed ? c.red : c.accent,
        marginTop: 4, fontWeight: 500,
      }}>
        {result.identity}
      </div>
      {result.spatial_location && (
        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 3 }}>
          {result.spatial_location}
        </div>
      )}
      {result.citation && (
        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2, fontStyle: 'italic' }}>
          {result.citation}
        </div>
      )}
      {result.reasoning && (
        <div style={{ fontSize: 10, color: c.textMuted, marginTop: 3 }}>
          {result.reasoning}
        </div>
      )}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
        gap: 4, marginTop: 8, fontSize: 11, color: c.text,
      }}>
        <div>Et: {result.Et_eV} eV</div>
        <div>σn: {result.sigma_n}</div>
        <div>σp: {result.sigma_p}</div>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginTop: 8,
      }}>
        <label style={{ fontSize: 11, color: c.textMuted }}>Nt (cm⁻³):</label>
        <input type="text" value={result._editedNt}
          onChange={e => onUpdateNt(idx, e.target.value)}
          style={{
            width: 70, padding: '2px 6px', fontSize: 11,
            background: c.input, color: c.inputText,
            border: `1px solid ${c.border}`, borderRadius: 4,
            fontFamily: 'IBM Plex Mono, monospace',
          }} />
        <button onClick={onAddToQueue}
          disabled={failed}
          style={{
            flex: 1, padding: '4px 8px',
            background: failed ? c.btnInactive : c.btnPrimary,
            color: failed ? c.btnInactiveText : c.btnPrimaryText,
            border: 'none', borderRadius: 4,
            cursor: failed ? 'not-allowed' : 'pointer',
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
          }}>
          + Add to Queue
        </button>
      </div>
    </div>
  );
}
